import type { JSX } from "react";

import type { IntentName } from "@/design";
import { cn } from "@/shared/lib/cn";

interface QuotaProgressBarProps {
  used: number;
  total: number;
}

const FILL: Record<IntentName, string> = {
  success: "bg-[var(--intent-success-fg)]",
  warning: "bg-[var(--intent-warning-fg)]",
  danger: "bg-[var(--intent-danger-fg)]",
  info: "bg-[var(--intent-info-fg)]",
  neutral: "bg-[var(--intent-neutral-fg)]",
};

/**
 * Quota health bar: green normally, yellow when <10% remains, red when
 * exhausted. Numerals are tabular so columns of quotas align.
 */
export function QuotaProgressBar({ used, total }: QuotaProgressBarProps): JSX.Element {
  const safeTotal = total > 0 ? total : 0;
  const pct = safeTotal > 0 ? Math.min(100, Math.round((used / safeTotal) * 100)) : 0;
  const remaining = safeTotal - used;
  const exhausted = safeTotal > 0 && remaining <= 0;
  const low = !exhausted && safeTotal > 0 && remaining / safeTotal < 0.1;
  const intent: IntentName = exhausted ? "danger" : low ? "warning" : "success";

  return (
    <div
      className="flex min-w-[130px] flex-col gap-1"
      data-testid="quota-progress"
      data-intent={intent}
    >
      <div className="h-[7px] w-full overflow-hidden rounded-full bg-[var(--surface-sunken)]">
        <div
          className={cn("h-full rounded-full transition-all", FILL[intent])}
          style={{ width: `${pct}%` }}
          data-testid="quota-progress-fill"
        />
      </div>
      <span className="num flex items-center gap-1 text-[11.5px] font-semibold tabular-nums text-[var(--text-secondary)]">
        {used.toLocaleString()} / {safeTotal.toLocaleString()}
        {exhausted && <span className="text-[var(--intent-danger-fg)]">✓</span>}
        {low && <span className="text-[var(--intent-warning-fg)]">⚠</span>}
      </span>
    </div>
  );
}
